/**
 * Download-page store destinations and the first-party store_click event.
 * Vendor pixels are not loaded here. A later change registers a hook and
 * beforeStoreRedirect waits for it, up to STORE_CLICK_REDIRECT_BUDGET_MS.
 */

import storeLinks from '@budgit/store-links';
import config from '../config';
import { getAccessToken } from '../utils/accessToken';
import { getAnalyticsConsent } from './analyticsConsent';

export const STORE_CLICK_REDIRECT_BUDGET_MS = 300;

const redirectHooks = [];
let autoRedirectClaimed = false;

export function claimAutoStoreRedirect() {
  if (autoRedirectClaimed) return false;
  autoRedirectClaimed = true;
  return true;
}

export function resetAutoStoreRedirectForTests() {
  autoRedirectClaimed = false;
}

export function registerStoreRedirectHook(hook) {
  if (typeof hook !== 'function') return () => {};
  redirectHooks.push(hook);
  return () => {
    const index = redirectHooks.indexOf(hook);
    if (index >= 0) redirectHooks.splice(index, 1);
  };
}

export function resetStoreRedirectHooksForTests() {
  redirectHooks.length = 0;
}

export function readAppStoreProviderToken() {
  return config.APP_STORE_PROVIDER_TOKEN || '';
}

export function detectMobileStorePlatform({
  userAgent = '',
  vendor = '',
  opera = '',
  msStream = false,
} = {}) {
  const ua = userAgent || vendor || opera || '';
  if (/android/i.test(ua)) return 'android';
  if (/iPad|iPhone|iPod/.test(ua) && !msStream) return 'ios';
  return null;
}

export function buildDownloadDestinations(search, { providerToken } = {}) {
  const utm = storeLinks.utmParamsFromSearch(search);
  const referrer = storeLinks.referrerFromUtm(utm);
  return {
    utm,
    ios: storeLinks.buildAppStoreListingUrl({
      providerToken,
      campaignToken: utm.utm_campaign,
    }),
    android: storeLinks.buildPlayStoreDetailsUrl({
      referrer: referrer || undefined,
    }),
  };
}

function analyticsEventUrl() {
  const base = process.env.REACT_APP_API_URL || 'http://localhost:4001/api/v1';
  return `${String(base).replace(/\/$/, '')}/analytics/event`;
}

export function postStoreClick(properties) {
  const body = JSON.stringify({
    event: 'store_click',
    properties: {
      appVersion: process.env.REACT_APP_VERSION || 'unknown',
      ...properties,
    },
  });
  const headers = { 'Content-Type': 'application/json' };
  try {
    const token = getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch (_) {
    // Anonymous store_click is still worth sending.
  }

  const url = analyticsEventUrl();
  try {
    if (typeof fetch === 'function') {
      fetch(url, { method: 'POST', body, headers, keepalive: true }).catch(() => {});
      return;
    }
  } catch (_) {
    // Fall through to sendBeacon.
  }

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    }
  } catch (_) {
    // Analytics must not block the store.
  }
}

function waitForRedirectHooks(hooks, context) {
  let timer;
  const budget = new Promise((resolve) => {
    timer = setTimeout(resolve, STORE_CLICK_REDIRECT_BUDGET_MS);
  });
  const work = Promise.all(hooks.map((hook) => {
    try {
      return Promise.resolve(hook(context)).catch(() => {});
    } catch (_) {
      return Promise.resolve();
    }
  })).then((value) => {
    clearTimeout(timer);
    return value;
  });
  return Promise.race([work, budget]);
}

/**
 * Post store_click, then give consented vendor hooks at most 300ms.
 * No hooks are registered in this change. Returns the store URL.
 * Does not navigate.
 */
export async function beforeStoreRedirect(platform, {
  surface,
  search,
  providerToken,
} = {}) {
  const destinations = buildDownloadDestinations(search, { providerToken });
  const url = platform === 'ios' ? destinations.ios : destinations.android;
  try {
    postStoreClick({ platform, surface, ...destinations.utm });
  } catch (_) {
    // The store still opens.
  }

  let consented = false;
  try {
    consented = getAnalyticsConsent() === 'granted';
  } catch (_) {
    consented = false;
  }
  const hooks = consented ? redirectHooks.slice() : [];
  if (hooks.length === 0) return url;

  await waitForRedirectHooks(hooks, {
    platform,
    surface,
    utm: destinations.utm,
    url,
  });
  return url;
}

/**
 * Open the store. noopener makes window.open return null even when the new
 * tab opened, so a null return must not location.assign this tab.
 */
export function openStoreUrl(url, { replace = false } = {}) {
  if (replace) {
    window.location.replace(url);
    return;
  }
  try {
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (_) {
    // Stay on budgit.app. The button href is still there.
  }
}
